import { GraphQLQuery } from "@aws-amplify/api";
import {
  FetchUserAttributesOutput,
  fetchUserAttributes,
} from "aws-amplify/auth";
import { generateClient } from "aws-amplify/api";
import {
  UpdateRecordingMutation,
  RecordingsByCIDQuery,
  RecordingsByLIDQuery,
  RecordingsByLIDQueryVariables,
} from "@/API";

const client = generateClient();

const useCreateRecording = () => {

  // NEW: Polling function that can be called AFTER S3 upload
  const pollForS3TriggerRecord = async (recordingData: { storagePath: string; name: string }, userInfo: FetchUserAttributesOutput) => {
    const maxAttempts = 10;
    let s3Recording = null;
    
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      if (attempt > 1) {
        const waitTime = Math.min(1000 * attempt, 8000);
        await new Promise(resolve => setTimeout(resolve, waitTime));
      }
      
      try {
        const { recordingsByLID } = await import("@/graphql/queries");
        const existingRecordingsResponse = await client.graphql<GraphQLQuery<RecordingsByLIDQuery>>({
          query: recordingsByLID,
          variables: { lID: userInfo["custom:location"] } as RecordingsByLIDQueryVariables,
        });
        
        const existingRecordings = existingRecordingsResponse.data?.recordingsByLID?.items || [];
        
        s3Recording = existingRecordings.find((rec) => 
          rec?.storagePath?.includes(recordingData.storagePath) &&
          !rec?.cID &&
          (new Date(rec.createdAt).getTime() > Date.now() - 10 * 60 * 1000)
        );
        
        if (s3Recording) {
          return s3Recording;
        }
      } catch (error) {
        // Continue polling on error
      }
    }
    
    return null;
  };

  const handleCreateRecording = async (
    recordingData: {
      name: string;
      duration: number;
      storagePath: string;
      useTemplate: boolean;
      caseId?: string;
      patientId?: string;
      templateId?: string;
    }
  ) => {
    // For QuickRecords (no caseId), S3Trigger Lambda WILL create the recording automatically
    // We should wait for S3Trigger and avoid creating duplicates
    const isQuickRecord = !recordingData.caseId;
    
    if (isQuickRecord) {
      return { 
        data: { 
          createRecording: {
            id: 'pending-s3trigger',
            name: recordingData.name,
            duration: recordingData.duration,
            storagePath: recordingData.storagePath,
            caseId: null,
            pendingS3Trigger: true
          }
        } 
      };
    }
    
    await new Promise(resolve => setTimeout(resolve, (recordingData.duration*1000)/3));
    
    // const userInfo: FetchUserAttributesOutput = await fetchUserAttributes();
    
    try {
      const { recordingsByCID } = await import("@/graphql/queries");
      const existingRecordingsResponse = await client.graphql<GraphQLQuery<RecordingsByCIDQuery>>({
        query: recordingsByCID,
        variables: { cID: recordingData.caseId },
      });
      
      const existingRecordings = existingRecordingsResponse.data?.recordingsByCID?.items || [];
      const s3Recording = existingRecordings.find(rec => 
        (rec?.storagePath?.includes(recordingData.storagePath) || 
         recordingData.storagePath.includes(rec?.storagePath || '')) &&
        rec?.storagePath?.includes('private/') && // S3Trigger creates with full S3 path
        (new Date(rec.createdAt).getTime() > Date.now() - 2 * 60 * 1000) // Within last 2 minutes
      );
      
      if (s3Recording) {
        try {
          // Update the S3 recording to have a clean path and ensure case_id is set
          const { updateRecording } = await import("@/graphql/mutations");
          await client.graphql<GraphQLQuery<UpdateRecordingMutation>>({
            query: updateRecording,
            variables: {
              input: {
                id: s3Recording.id,
                storagePath: recordingData.storagePath, // Update to clean path
                cID: recordingData.caseId, // Ensure case_id is properly set
                _version: s3Recording._version
              }
            }
          });
          
          try {
            const { supabase } = await import("@/lib/supabaseClient");
            await supabase
              .from('case_recordings')
              .update({
                case_id: recordingData.caseId,
                file_path: recordingData.storagePath
              })
              .eq('id', s3Recording.id);
          } catch (supabaseError) {
            // Supabase update failed but Amplify update succeeded
          }
          
          // Sync the updated recording to Supabase via migration service
          const updatedRecording = {
            ...s3Recording,
            storagePath: recordingData.storagePath, // Clean path
            cID: recordingData.caseId // Ensure case_id is set in response
          };
          
          if (updatedRecording.text && updatedRecording.text.length > 0) {
          try {
            const { DatabaseMigrationAdapter } = await import("@/services/migration");
            const migrationAdapter = DatabaseMigrationAdapter.getInstance();
            await migrationAdapter.handleRecordingOperation('CREATE', updatedRecording as Record<string, unknown>, { data: { createRecording: updatedRecording } } as unknown as Record<string, unknown>);
          } catch (syncError) {
              // Sync failed but recording creation succeeded
            }
          }
          
          return {
            data: {
              createRecording: updatedRecording
            }
          };
        } catch (updateError) {
          // Fall through to create a new recording if update fails
        }
      }
    } catch (error) {
      return null;
    }
    
    return null;
  };

  return { handleCreateRecording, pollForS3TriggerRecord };
};

export default useCreateRecording; 